import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  listPending,
  approveRequest,
  rejectRequest,
  managerCancelJoinRequest,
} from "../api/joinRequests";
import { getMySalons } from "../api/salons";
import { formatSalonLabel } from "../utils/formatSalonLabel";
import { formatInTimeZone } from "../utils/datetime";
import ModalCloseX from "../components/common/ModalCloseX";

const STATUS_PILL_STYLES = {
  PENDING: "bg-amber-100 text-amber-700 border-amber-200",
  APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  REJECTED: "bg-slate-100 text-slate-600 border-slate-200",
  CANCELED: "bg-slate-100 text-slate-600 border-slate-200",
  EXPIRED: "bg-orange-100 text-orange-700 border-orange-200",
};

const normalizeStatus = (value) => (value || "").toUpperCase();
const DEFAULT_TIMEZONE = "America/Toronto";
const RECENT_REQUEST_DAYS = 30;
const APPROVAL_ROLE_OPTIONS = [
  { value: "technician", label: "Technician" },
  { value: "reception", label: "Reception" },
  { value: "manager", label: "Manager" },
  { value: "apprentice", label: "Apprentice" },
];

export default function ManagerJoinRequests() {
  const navigate = useNavigate();
  const { token, is_super_admin, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [salonFilter, setSalonFilter] = useState("");
  const [salonOptions, setSalonOptions] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [approvalRoles, setApprovalRoles] = useState({});

  const fetchRequests = useCallback(
    async (filterSalonId) => {
      if (!token) return;
      setLoading(true);
      setError("");
      try {
        const salonParam = filterSalonId ? Number(filterSalonId) : undefined;
        const data = await listPending(token, salonParam, {
          includeRecent: true,
          recentDays: RECENT_REQUEST_DAYS,
        });
        setRequests(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
        setRequests([]);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const loadSalonOptions = useCallback(async () => {
    if (!is_super_admin || !token) return;
    setFilterLoading(true);
    try {
      const data = await getMySalons(token);
      setSalonOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      setSalonOptions([]);
    } finally {
      setFilterLoading(false);
    }
  }, [is_super_admin, token]);

  useEffect(() => {
    fetchRequests(salonFilter);
  }, [fetchRequests, salonFilter]);

  useEffect(() => {
    loadSalonOptions();
  }, [loadSalonOptions]);

  const handleAction = async (requestId, action) => {
    if (!token) return;
    setActionLoading(requestId);
    setError("");
    try {
      if (action === "approve") {
        await approveRequest(
          token,
          requestId,
          approvalRoles[requestId] || "technician"
        );
      } else if (action === "reject") {
        await rejectRequest(token, requestId);
      } else if (action === "cancel") {
        await managerCancelJoinRequest(token, requestId);
      }
      await fetchRequests(salonFilter);
    } catch (err) {
      const raw = err?.message || "Unable to update request";
      const normalized = raw.toLowerCase();
      if (normalized.includes("permission")) {
        setError("You don't have permission to manage requests for this salon.");
      } else if (normalized.includes("pending")) {
        setError("Request is no longer pending.");
      } else if (normalized.includes("expired")) {
        setError("Request expired and cannot be processed.");
      } else if (normalized.includes("canceled")) {
        setError("Request was canceled and cannot be processed.");
      } else if (normalized.includes("does not belong")) {
        if (salonFilter) {
          setError("Request does not belong to the selected salon.");
        }
      } else {
        setError(raw);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleFilterChange = (event) => {
    setSalonFilter(event.target.value);
  };

  const toggleMessage = (requestId) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [requestId]: !prev[requestId],
    }));
  };

  const formatRequestedOn = (request) => {
    const tz =
      request?.salon_timezone ||
      request?.business_timezone ||
      user?.salon_timezone ||
      DEFAULT_TIMEZONE;
    return formatInTimeZone(
      request?.created_at,
      tz,
      { year: "numeric", month: "2-digit", day: "2-digit" },
      "en-CA"
    );
  };

  const selectedSalonLabel = salonFilter
    ? formatSalonLabel(
      salonOptions.find((salon) => String(salon.id) === String(salonFilter))
    ) || "Selected salon"
    : "All salons";

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="manageSectionShell">
      <div className="manageSectionWide manage-section-wide manage-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Salon Join Requests
            </h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[340px_minmax(0,1fr)]">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                    Filter
                  </h2>
                  <p className="text-sm text-slate-500">
                    Narrow requests by salon.
                  </p>
                </div>

                {is_super_admin ? (
                  <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Salon
                    </label>
                    <select
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:bg-slate-50"
                      value={salonFilter}
                      onChange={handleFilterChange}
                      disabled={filterLoading}
                    >
                      <option value="">All salons</option>
                      {salonOptions.map((salon) => (
                        <option key={salon.id} value={salon.id}>
                          {formatSalonLabel(salon)}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-2 text-sm text-slate-500">
                    Showing requests for your current salon.
                  </div>
                )}

                <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
                  <p>
                    Requests shown:{" "}
                    <span className="font-semibold text-slate-900">
                      {loading ? "-" : requests.length}
                    </span>
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Includes pending plus the last {RECENT_REQUEST_DAYS} days of approved,
                    rejected, canceled, and expired requests.
                  </p>
                  {is_super_admin && (
                    <p className="mt-1 text-xs text-slate-500">
                      Scope: {selectedSalonLabel}
                    </p>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 lg:p-6 space-y-4 min-w-0">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                    Recent Request Activity
                  </h2>
                  <p className="text-sm text-slate-500">
                    Approve pending requests and review recent approved, rejected, canceled,
                    and expired requests.
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto lg:overflow-visible">
                  <table className="w-full table-fixed text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                      <tr>
                        <th className="px-3 py-2 text-left w-40">Salon</th>
                        <th className="px-3 py-2 text-left w-44">Requester</th>
                        <th className="px-3 py-2 text-left w-32">Requested On</th>
                        <th className="px-3 py-2 text-left">Message</th>
                        <th className="px-3 py-2 text-left w-28">Status</th>
                        <th className="px-3 py-2 text-right w-56">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {loading && (
                        <tr>
                          <td className="px-3 py-4 text-slate-500" colSpan={6}>
                            Loading requests...
                          </td>
                        </tr>
                      )}

                      {!loading && requests.length === 0 && (
                        <tr>
                          <td className="px-3 py-4" colSpan={6}>
                            <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-3 text-sm text-slate-500">
                              {is_super_admin
                                ? "No recent requests for the selected filter."
                                : "No recent requests in this salon."}
                            </div>
                          </td>
                        </tr>
                      )}

                      {!loading &&
                        requests.map((request) => {
                          const normalizedStatus = normalizeStatus(request.status);
                          const pillClasses =
                            STATUS_PILL_STYLES[normalizedStatus] ||
                            "bg-slate-100 text-slate-600 border-slate-200";
                          const isBusy = actionLoading === request.id;
                          const isPending = normalizedStatus === "PENDING";
                          const messageText = (request.message || "").trim();
                          const showMessageToggle = messageText.length > 90;
                          const isExpanded = Boolean(expandedMessages[request.id]);

                          return (
                            <tr key={request.id} className="align-top hover:bg-slate-50/70">
                              <td className="px-3 py-2.5 font-semibold text-slate-900 break-words">
                                {request.salon_name || "--"}
                              </td>
                              <td className="px-3 py-2.5 text-slate-700 break-words">
                                {request.user_name || request.user_email || "Unknown technician"}
                              </td>
                              <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">
                                {formatRequestedOn(request)}
                              </td>
                              <td className="px-3 py-2.5 text-slate-600">
                                {messageText ? (
                                  <div className="space-y-1">
                                    <span
                                      className={
                                        isExpanded
                                          ? "block whitespace-normal break-words"
                                          : "block truncate"
                                      }
                                      title={!isExpanded ? messageText : undefined}
                                    >
                                      {messageText}
                                    </span>
                                    {showMessageToggle && (
                                      <button
                                        type="button"
                                        onClick={() => toggleMessage(request.id)}
                                        className="text-xs font-semibold text-slate-600 hover:text-slate-900"
                                      >
                                        {isExpanded ? "Show less" : "Show more"}
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-slate-400">--</span>
                                )}
                              </td>
                              <td className="px-3 py-2.5">
                                <span
                                  className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${pillClasses}`}
                                >
                                  {normalizedStatus}
                                </span>
                              </td>
                              <td className="px-3 py-2.5">
                                {isPending ? (
                                  <div className="flex flex-wrap items-center justify-end gap-2">
                                    <select
                                      className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700"
                                      value={approvalRoles[request.id] || "technician"}
                                      onChange={(event) =>
                                        setApprovalRoles((prev) => ({
                                          ...prev,
                                          [request.id]: event.target.value,
                                        }))
                                      }
                                      disabled={isBusy}
                                    >
                                      {APPROVAL_ROLE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                    <button
                                      type="button"
                                      disabled={isBusy}
                                      onClick={() => handleAction(request.id, "approve")}
                                      className="rounded-xl bg-emerald-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-70"
                                    >
                                      {isBusy ? "Approving..." : "Approve"}
                                    </button>
                                    <button
                                      type="button"
                                      disabled={isBusy}
                                      onClick={() => handleAction(request.id, "reject")}
                                      className="rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-70"
                                    >
                                      {isBusy ? "Rejecting..." : "Reject"}
                                    </button>
                                    <button
                                      type="button"
                                      disabled={isBusy}
                                      onClick={() => handleAction(request.id, "cancel")}
                                      className="rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-70"
                                    >
                                      {isBusy ? "Canceling..." : "Cancel"}
                                    </button>
                                  </div>
                                ) : (
                                  <div className="text-right text-xs font-medium text-slate-400">
                                    No actions
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

